import { forwardRef, type ReactNode } from 'react';
import type { ThreadConversionChange } from '@mg/shared';
import { cn } from '../../lib/cn';

/**
 * What the AI added or reworded, in "Show changes": green, and underlined, so it still shows where
 * forced colours drop the fill. The banner's legend draws its "Added" sample with the same classes.
 */
export const ADDED_MARK =
  'bg-success-tint text-ink underline decoration-success dark:bg-success-dark-tint dark:decoration-success-dark';
/** What the AI removed, in "Show changes": red and struck through. The legend's "Removed" sample too. */
export const REMOVED_MARK =
  'bg-red-50 text-red-700 line-through dark:bg-error-dark-tint dark:text-error-dark';
/**
 * The red border of a post's text that is over its platform's limit. Keyed to `aria-invalid`, so it
 * is drawn after the plain border colour whatever order the stylesheet puts the two in (`cn` only
 * joins class names); a box gets the class only while it is over.
 */
export const OVER_LIMIT_BORDER =
  'aria-[invalid=true]:border-red-600 dark:aria-[invalid=true]:border-error-dark';

/**
 * A character of a script written without spaces between its words - Han, Hiragana, Katakana, Thai,
 * Lao, Khmer, Myanmar, the server's SPACELESS (word-align.ts) - or of the punctuation and full-width
 * forms written with them (。、「」ー・ and ，！？：（）), which Unicode files under no script.
 */
const SPACELESS =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}\u3001-\u303F\u30A0-\u30FF\uFF01-\uFF9F\uFFE0-\uFFE6]/u;
const STARTS_SPACELESS = new RegExp(`^${SPACELESS.source}`, 'u');
const ENDS_SPACELESS = new RegExp(`${SPACELESS.source}$`, 'u');

/** Whether the 'same' and 'added' parts, in order, spell `text` - the one state they describe it in. */
function spells(changes: readonly ThreadConversionChange[], text: string): boolean {
  return (
    changes
      .filter((change) => change.kind !== 'removed')
      .map((change) => change.text)
      .join('') === text
  );
}

/** The parts of a post, marked. Whitespace is the post's own and is never marked. */
function marked(changes: readonly ThreadConversionChange[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  // What has been drawn so far, to know whether a removed word needs a space before it.
  let drawn = '';
  changes.forEach((change, i) => {
    if (change.kind === 'same') {
      nodes.push(change.text);
      drawn += change.text;
      return;
    }
    if (change.kind === 'added') {
      // A line break inside an added run is not marked (the AI joining or keeping paragraphs is not
      // a change of words), and neither are the spaces around the words.
      change.text.split(/(\s*\n\s*)/).forEach((piece, j) => {
        if (j % 2 === 1) {
          nodes.push(piece);
          return;
        }
        const [, lead, words, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(piece) ?? ['', '', piece, ''];
        if (lead) nodes.push(lead);
        if (words) {
          nodes.push(
            <ins key={`${i}.${j}`} title="Added" className={ADDED_MARK}>
              <span className="sr-only">Added: </span>
              {words}
            </ins>,
          );
        }
        if (trail) nodes.push(trail);
      });
      drawn += change.text;
      return;
    }
    const words = change.text.trim();
    if (!words) return;
    // Words the post does not keep stand apart from their neighbours: one space before them unless
    // what is drawn already ends in one, and one after them when anything follows that does not
    // start with one - but none beside a neighbour written without spaces, where the person wrote
    // none ("今日は~~とても良い~~天気"). The spaces sit inside the <del>, so the post's own text,
    // read without it, is the post exactly.
    const next = changes
      .slice(i + 1)
      .find((c) => (c.kind === 'removed' ? c.text.trim() !== '' : c.text !== ''));
    const following = next && (next.kind === 'removed' ? next.text.trim() : next.text);
    const before = drawn !== '' && !/\s$/.test(drawn) && !ENDS_SPACELESS.test(drawn);
    const after = !!following && !/^\s/.test(following) && !STARTS_SPACELESS.test(following);
    nodes.push(
      <del key={i} title="Removed" className="no-underline">
        {before && ' '}
        <span className={REMOVED_MARK}>
          <span className="sr-only">Removed: </span>
          {words}
        </span>
        {after && ' '}
      </del>,
    );
    drawn += `${before ? ' ' : ''}${words}${after ? ' ' : ''}`;
  });
  return nodes;
}

/**
 * "Show changes" for one post of a thread the AI made: the post's text, read-only, in place of its
 * text box and in the same box (so the card does not jump), with what the AI added marked green and
 * what it removed struck through in red, word by word. Labelled like the text box it stands in for,
 * and focusable, so it can be read from the keyboard.
 *
 * The marks are drawn only while they still spell the post's text exactly; otherwise - the post was
 * edited, or the server could not line it up with the original (`changes` empty) - its text is
 * shown plain. The marks are never in the post itself: what is saved, previewed and published is
 * `text` alone.
 */
export const ThreadPostChanges = forwardRef<
  HTMLDivElement,
  {
    /** 1-based, as people count. */
    number: number;
    /** The post's text as it stands. */
    text: string;
    /** The AI's changes for this post, or null to show the text plain. */
    changes: ThreadConversionChange[] | null;
    /** The post is over its platform's limit: the box is red, as the text box would be. */
    over: boolean;
    /** The ids of what describes the box: the over-limit line and the counter, while it is over. */
    describedBy?: string;
  }
>(function ThreadPostChanges({ number, text, changes, over, describedBy }, ref) {
  return (
    <div
      ref={ref}
      role="textbox"
      aria-readonly
      aria-multiline
      aria-label={`Post ${number} text`}
      aria-invalid={over || undefined}
      aria-describedby={describedBy}
      tabIndex={0}
      className={cn(
        'focus-ring block min-h-[6.125rem] w-full whitespace-pre-wrap break-words rounded-lg border border-line-input bg-page px-3 py-2 text-sm text-ink',
        over && OVER_LIMIT_BORDER,
      )}
    >
      {changes != null && spells(changes, text) ? marked(changes) : text}
    </div>
  );
});
